import React from "react";
import type { CalendarOperation } from "../types";
import EventCard from "./EventCard";
import Spinner from "./Spinner";

interface EventConfirmationProps {
  operations: CalendarOperation[];
  onConfirm: () => void;
  onCancel: () => void;
  isAdding: boolean;
  operationAttachments: Map<number, File[]>;
  onAttachmentsChange: (operationIndex: number, files: File[]) => void;
}

const EventConfirmation: React.FC<EventConfirmationProps> = ({
  operations,
  onConfirm,
  onCancel,
  isAdding,
  operationAttachments,
  onAttachmentsChange,
}) => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <h3 className="text-xl font-semibold text-center text-rose-accent mb-4">
        Confirm Calendar Changes
      </h3>
      <p className="text-center text-text-secondary mb-6">
        I&apos;ve prepared the following {operations.length} change(s). Please review before
        applying them to your calendar.
      </p>
      <div className="space-y-4 mb-6 overflow-y-auto flex-1 min-h-0">
        {operations.map((op, index) => (
          <EventCard
            key={index}
            operation={op}
            attachments={operationAttachments.get(index) || []}
            onAttachmentsChange={(files) => onAttachmentsChange(index, files)}
            disabled={isAdding}
          />
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
        <button
          onClick={onConfirm}
          disabled={isAdding}
          className="w-full flex justify-center items-center bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-sm transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-navy-darkest disabled:bg-green-900/50 disabled:cursor-not-allowed disabled:text-text-muted"
        >
          {isAdding ? (
            <>
              <Spinner />
              <span className="ml-2">Applying...</span>
            </>
          ) : (
            `Confirm ${operations.length} Change(s)`
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={isAdding}
          className="w-full bg-navy-mid hover:bg-navy-light text-white font-bold py-3 px-4 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-rose-accent focus:ring-offset-2 focus:ring-offset-navy-darkest"
        >
          Cancel / Edit
        </button>
      </div>
    </div>
  );
};

export default EventConfirmation;
